import React, { useEffect, useState } from 'react';

import {
  Alert,
  Card,
  Descriptions,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  notification,
  Radio,
  Skeleton,
  Table,
  Typography,
} from 'antd';

import {
  add_units,
  get_units,
  getProducts,
  remove_units,
  save_payment,
  send_payment,
} from '../../api/module/product_approval';
import { catchException } from '../../utils/helpers/helpers';
import { CustomButton } from '../../components';
import { CustomTooltip } from '../../components/CustomTooltip';

import MESSAGES from '../../enums/Messages';

import dayjs from 'dayjs';
import 'dayjs/locale/es';
import EllipsisModal from '../../components/EllipsisModal';

const { TextArea } = Input;
const { Title } = Typography;

const nonEditableStates = [96, 97, 99, 100, 101];

export function PAProductForm({
  initialValues,
  ms,
  disabled,
  currencyType,
  paymentStatus,
  payNumber,
  saveErrors,
}) {
  const [selfDisabled] = useState(
    disabled ||
      (paymentStatus !== 107 &&
        nonEditableStates.includes(initialValues.estado_aprobacion)),
  );

  return (
    <Card
      size={'small'}
      bordered={false}
      headStyle={{
        justifyContent: 'space-evenly',
        display: 'flex',
      }}
      title={
        <Title level={5} className="color-primary">
          PAGO No. {payNumber} -{' '}
          {initialValues.tipo.startsWith('P') ? 'Producto' : 'Gasto'}{' '}
          {initialValues.tipo.endsWith('F') ? 'Fijo' : 'Variable'} No.{' '}
          {initialValues.codigo}
        </Title>
      }
    >
      {initialValues.tipo.endsWith('F') ? (
        <>
          <Descriptions
            layout="horizontal"
            column={2}
            bordered
            labelStyle={{ maxWidth: '8rem' }}
          >
            <Descriptions.Item label="Producto" span={2}>
              <EllipsisModal
                title="Producto"
                text={initialValues.producto_nombre}
              />
            </Descriptions.Item>
            <Descriptions.Item label="Valor del producto">
              {ms}{' '}
              {initialValues.valor_total
                ?.toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            </Descriptions.Item>
            <Descriptions.Item label="Estado del pago de este producto">
              {/* TODO: reference to disbursement */}
              Pendiente
            </Descriptions.Item>
            <Descriptions.Item
              label="Requiere evidencias"
              span={initialValues.tipo.startsWith('P') ? 1 : 2}
            >
              {/* TODO: button with htmltype button that shows evidence */}
              {initialValues.requiere_evidencias ? 'Pendiente adjuntar' : 'No'}
            </Descriptions.Item>
            {initialValues.tipo.startsWith('P') ? (
              <Descriptions.Item label="Es periódico">
                {initialValues.periodico ? 'Si' : 'No'}
              </Descriptions.Item>
            ) : null}
          </Descriptions>
          <div style={{ border: '1px solid #f0f0f0' }}>
            <Form.Item
              name={[initialValues.id, 'estado_aprobacion']}
              rules={[
                {
                  required: true,
                  message: 'Este campo es requerido',
                },
              ]}
              style={{
                marginTop: 24,
                marginLeft: 10,
                marginRight: 10,
              }}
            >
              <Radio.Group
                disabled={selfDisabled}
                size="large"
                style={{ justifyContent: 'space-evenly', display: 'flex' }}
              >
                <Radio value={100}>Aprobado total</Radio>
                <Radio
                  value={99}
                  onClick={() =>
                    Modal.info({
                      title: 'Recuerde que...',
                      content: (
                        <>
                          Si elije este tipo de aprobación debe realizar una de
                          las siguientes actividades:{' '}
                          <ul>
                            <li>
                              Modificación contractual para agregar el producto
                              nuevo y completarlo
                            </li>
                            <li>
                              Un acta de terminación anticipada en caso de
                              finalizar el contrato y no se necesite el producto
                              completo
                            </li>
                          </ul>
                        </>
                      ),
                    })
                  }
                >
                  Aprobado incompleto{' '}
                  <CustomTooltip title="Entrega de evidencias incompletas, no se pagará la totalidad del producto." />
                </Radio>
                <Radio value={98}>
                  Condicionado{' '}
                  <CustomTooltip title="No se entrega la totalidad del producto, pero se aprueba gestión del pago, con el compromiso por parte del contratista de entrega total antes del próximo pago." />
                </Radio>
                <Radio
                  value={97}
                  disabled={
                    selfDisabled ||
                    (paymentStatus !== 107 &&
                      initialValues.estado_aprobacion === 98)
                  }
                >
                  No aprobado{' '}
                  <CustomTooltip title="Producto no aprobado por la supervisión del contrato por no cumplir con lo pactado entre las partes, es un estado definitivo." />
                </Radio>
                <Radio
                  value={96}
                  disabled={
                    selfDisabled ||
                    (paymentStatus !== 107 &&
                      initialValues.estado_aprobacion === 98)
                  }
                >
                  No entregado{' '}
                  <CustomTooltip title="Producto que el contratista no entregará durante la ejecución del contrato, es un estado definitivo." />
                </Radio>
                <Radio
                  value={95}
                  disabled={
                    selfDisabled ||
                    (paymentStatus !== 107 &&
                      initialValues.estado_aprobacion === 98)
                  }
                >
                  Pendiente{' '}
                  <CustomTooltip title="Producto que será ejecutado en fechas posteriores." />
                </Radio>
              </Radio.Group>
            </Form.Item>
          </div>
          <Form.Item
            noStyle
            dependencies={[[initialValues.id, 'estado_aprobacion']]}
          >
            {({ getFieldValue }) => {
              const ea = getFieldValue([initialValues.id, 'estado_aprobacion']);
              if (
                (paymentStatus !== 107 &&
                  initialValues.estado_aprobacion === 98) ||
                (ea && ea !== 95 && ea !== 100)
              ) {
                return (
                  <Descriptions
                    layout="horizontal"
                    column={1}
                    bordered
                    labelStyle={{ maxWidth: '8rem' }}
                    contentStyle={{ padding: '25px 25px 1px 25px' }}
                  >
                    {ea === 99 && (
                      <Descriptions.Item label="Valor a pagar">
                        <Form.Item
                          name={[initialValues.id, 'valor_a_pagar']}
                          rules={[
                            {
                              required: true,
                              message: 'Este campo es obligatorio!',
                            },
                            {
                              validator: (_, value) => {
                                if (!value && value !== 0) {
                                  return Promise.resolve();
                                } else if (isNaN(value)) {
                                  return Promise.reject(
                                    new Error('Debe ser un valor numérico'),
                                  );
                                }
                                if (value < 1) {
                                  return Promise.reject(
                                    new Error('Debe ser mayor o igual a 1'),
                                  );
                                }

                                if (value >= initialValues.valor_total) {
                                  return Promise.reject(
                                    new Error(
                                      'Debe ser menor al valor del producto',
                                    ),
                                  );
                                }

                                if (currencyType && currencyType !== 'COP') {
                                  if (
                                    value.toString().includes('.') &&
                                    value.toString().split('.', 2)[1].length > 2
                                  ) {
                                    return Promise.reject(
                                      new Error(
                                        'Debe ser un valor con máximo dos decimales',
                                      ),
                                    );
                                  }
                                } else if (value.toString().includes('.')) {
                                  return Promise.reject(
                                    new Error('Debe ser un valor entero'),
                                  );
                                }

                                return Promise.resolve();
                              },
                            },
                          ]}
                        >
                          <InputNumber
                            disabled={selfDisabled}
                            style={{ width: '100%' }}
                            formatter={(value) =>
                              `${ms} ${value}`.replace(
                                /\B(?=(\d{3})+(?!\d))/g,
                                ',',
                              )
                            }
                            parser={(value) =>
                              value.replace(ms, '').replace(/\$\s?|(,*)/g, '')
                            }
                          />
                        </Form.Item>
                      </Descriptions.Item>
                    )}
                    <Descriptions.Item label="Justificación del valor a pagar">
                      <Form.Item
                        name={[initialValues.id, 'justificacion']}
                        rules={[
                          {
                            required: true,
                            message: 'Este campo es requerido',
                          },
                        ]}
                      >
                        <TextArea
                          disabled={selfDisabled}
                          rows={2}
                          bordered
                          allowClear
                        />
                      </Form.Item>
                    </Descriptions.Item>
                  </Descriptions>
                );
              }
              return null;
            }}
          </Form.Item>
        </>
      ) : initialValues.tipo.endsWith('V') ? (
        <>
          <Descriptions
            layout="horizontal"
            column={2}
            bordered
            labelStyle={{ maxWidth: '13rem' }}
          >
            <Descriptions.Item label="Producto" span={2}>
              <EllipsisModal
                title="Producto"
                text={initialValues.producto_nombre}
              />
            </Descriptions.Item>
            <Descriptions.Item label="Unidad">
              {initialValues.nombre_unitario}
            </Descriptions.Item>
            <Descriptions.Item label="Valor unidad">
              {ms}{' '}
              {initialValues.valor_unitario
                .toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            </Descriptions.Item>
            <Descriptions.Item label="Unidades máximas a pagar">
              {initialValues.unidades}
            </Descriptions.Item>
            <Descriptions.Item label="Valor máximo a pagar">
              {ms}{' '}
              {(initialValues.valor_unitario * initialValues.unidades)
                .toFixed(2)
                .toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            </Descriptions.Item>
            <Descriptions.Item
              label="Unidades aprobadas para este pago"
              contentStyle={{ padding: '25px 25px 1px 25px' }}
            >
              <Form.Item
                name={[initialValues.id, 'unidades_a_pagar']}
                rules={[
                  {
                    required: true,
                    message: 'Este campo es obligatorio!',
                  },
                  {
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.resolve();
                      } else if (isNaN(value)) {
                        return Promise.reject(
                          new Error('Debe ser un valor numérico'),
                        );
                      }
                      if (value < 0) {
                        return Promise.reject(new Error('Debe ser positivo'));
                      }
                      if (value > initialValues.unidades) {
                        return Promise.reject(
                          new Error(
                            'El producto no tiene suficientes unidades por pagar',
                          ),
                        );
                      }
                      if (
                        value.toString().includes('.') &&
                        value.toString().split('.', 2)[1].length > 2
                      ) {
                        return Promise.reject(
                          new Error(
                            'Debe ser un valor con máximo dos decimales',
                          ),
                        );
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber disabled={selfDisabled} />
              </Form.Item>
            </Descriptions.Item>
            <Descriptions.Item label="Valor para este pago">
              <Form.Item
                noStyle
                dependencies={[
                  [initialValues.id, 'unidades_a_pagar'],
                  [initialValues.id, 'estado_aprobacion'],
                ]}
              >
                {({ getFieldValue }) => {
                  const up =
                    getFieldValue([initialValues.id, 'unidades_a_pagar']) || 0;
                  const ea = getFieldValue([
                    initialValues.id,
                    'estado_aprobacion',
                  ]);
                  if ([95, 96].includes(ea)) return `${ms} 0`;
                  return `${ms} ${(up * initialValues.valor_unitario)
                    .toFixed(2)
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
                }}
              </Form.Item>
            </Descriptions.Item>
            <Descriptions.Item label="Estado del pago de este producto">
              {/* TODO: reference to disbursement */}
              Pendiente
            </Descriptions.Item>
            <Descriptions.Item label="Requiere evidencias">
              {/* TODO: button with htmltype button that shows evidence */}
              {initialValues.requiere_evidencias ? 'Pendiente adjuntar' : 'No'}
            </Descriptions.Item>
          </Descriptions>

          <div style={{ border: '1px solid #f0f0f0' }}>
            <Form.Item
              noStyle
              dependencies={[[initialValues.id, 'unidades_a_pagar']]}
            >
              {({ getFieldValue }) => {
                const up =
                  getFieldValue([initialValues.id, 'unidades_a_pagar']) || 0;
                const t = up === initialValues.unidades ? 2 : up === 0 ? 0 : 1;
                return (
                  <Form.Item
                    name={[initialValues.id, 'estado_aprobacion']}
                    rules={[
                      {
                        required: true,
                        message: 'Este campo es requerido',
                      },
                    ]}
                    style={{
                      marginTop: 24,
                      marginLeft: 10,
                      marginRight: 10,
                    }}
                  >
                    <Radio.Group
                      disabled={selfDisabled}
                      size="large"
                      style={{
                        justifyContent: 'space-evenly',
                        display: 'flex',
                      }}
                    >
                      <Radio value={100} disabled={selfDisabled || t !== 2}>
                        Aprobado total
                      </Radio>
                      <Radio value={101} disabled={selfDisabled || t !== 1}>
                        Aprobado parcial{' '}
                        <CustomTooltip title="Ejecución de unidades inferior a las máximas a pagar para este pago." />
                      </Radio>
                      <Radio
                        value={98}
                        disabled={
                          selfDisabled ||
                          t === 0 ||
                          (paymentStatus !== 107 &&
                            initialValues.estado_aprobacion === 98)
                        }
                      >
                        Condicionado{' '}
                        <CustomTooltip title="No se entrega la totalidad del producto, pero se aprueba gestión del pago, con el compromiso por parte del contratista de entrega total antes del próximo pago." />
                      </Radio>
                      <Radio
                        value={96}
                        disabled={
                          selfDisabled ||
                          t !== 0 ||
                          (paymentStatus !== 107 &&
                            initialValues.estado_aprobacion === 98)
                        }
                      >
                        No entregado{' '}
                        <CustomTooltip title="Producto que el contratista no entregará durante la ejecución del contrato, es un estado definitivo." />
                      </Radio>
                      <Radio
                        value={95}
                        disabled={
                          selfDisabled ||
                          t !== 0 ||
                          (paymentStatus !== 107 &&
                            initialValues.estado_aprobacion === 98)
                        }
                      >
                        Pendiente{' '}
                        <CustomTooltip title="Producto que será ejecutado en fechas posteriores." />
                      </Radio>
                    </Radio.Group>
                  </Form.Item>
                );
              }}
            </Form.Item>
          </div>
          <Form.Item
            noStyle
            dependencies={[
              [initialValues.id, 'estado_aprobacion'],
              [initialValues.id, 'unidades_a_pagar'],
            ]}
          >
            {({ getFieldValue }) => {
              const ea = getFieldValue([initialValues.id, 'estado_aprobacion']);
              if (
                (paymentStatus !== 107 &&
                  initialValues.estado_aprobacion === 98) ||
                (ea && ea !== 95 && ea !== 100)
              ) {
                return (
                  <Descriptions
                    layout="horizontal"
                    column={1}
                    bordered
                    labelStyle={{ maxWidth: '8rem' }}
                    contentStyle={{ padding: '25px 25px 1px 25px' }}
                  >
                    <Descriptions.Item label="Justificación de aprobación">
                      <Form.Item
                        name={[initialValues.id, 'justificacion']}
                        rules={[
                          {
                            required: true,
                            message: 'Este campo es requerido',
                          },
                        ]}
                      >
                        <TextArea
                          disabled={selfDisabled}
                          rows={2}
                          bordered
                          allowClear
                        />
                      </Form.Item>
                    </Descriptions.Item>
                  </Descriptions>
                );
              }
              return null;
            }}
          </Form.Item>
        </>
      ) : null}
      {saveErrors && (
        <Alert
          type="error"
          style={{ marginTop: '1rem' }}
          description={
            <>
              <ul>
                {Object.values(saveErrors)
                  .flat()
                  .map((ei) => (
                    <li>{ei}</li>
                  ))}
              </ul>
            </>
          }
        />
      )}
    </Card>
  );
}

export function PAProductSTForm({
  paymentID,
  initialValues,
  ms,
  disabled,
  referenceValue,
  acumulatedValue,
  paymentStatus,
  payNumber,
  onAdd,
  onRemove,
  unitsEndpoint = undefined,
  onUnsaved,
}) {
  const [selfDisabled] = useState(
    disabled || (paymentStatus && paymentStatus !== 107),
  );
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const [modalData, setModalData] = useState();
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    form.resetFields();
  }, [initialValues.unidades_a_pagar]);

  const onSelfFinish = async (values) => {
    setLoading(true);
    await onAdd({ ...values, producto: initialValues.id });
    setLoading(false);
  };

  const onShowUnits = async () => {
    try {
      setModalOpen(true);
      if (unitsEndpoint) {
        const res = await unitsEndpoint(paymentID, initialValues.id);
        setModalData(res.data);
        return;
      }
      const res = await get_units(paymentID, initialValues.id);
      setModalData(res.data);
    } catch (error) {
      catchException(error);
      setModalOpen(false);
    }
  };

  const onSelfRemove = async (uh) => {
    setLoading(true);
    setModalData();
    await onRemove({ uh }, initialValues.id);
    onShowUnits();
    setLoading(false);
  };

  const unitsColumns = [
    {
      title: 'Fecha',
      dataIndex: 'created_at',
      align: 'center',
      render: (t) => dayjs(t).format('YYYY-MM-DD'),
    },
    {
      title: 'Cantidad',
      dataIndex: 'cantidad',
      align: 'center',
    },
    {
      title: 'Valor',
      dataIndex: 'valor',
      align: 'center',
      render: (t) =>
        `${ms} ${t.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`,
    },
    {
      title: 'Acciones',
      dataIndex: 'id',
      align: 'center',
      render: (t) => (
        <CustomButton
          text="Eliminar"
          danger
          type="danger"
          htmlType="button"
          disabled={selfDisabled}
          onClick={() => onSelfRemove(t)}
        />
      ),
    },
  ];

  return (
    <Card
      size={'small'}
      bordered={false}
      headStyle={{
        justifyContent: 'space-evenly',
        display: 'flex',
      }}
      title={
        <Title level={5} className="color-primary">
          PAGO No. {payNumber} -{' '}
          {initialValues.tipo.startsWith('P') ? 'Producto' : 'Gasto'} Variable
          SIN TOPE
        </Title>
      }
    >
      <Form
        form={form}
        onFinish={onSelfFinish}
        initialValues={initialValues}
        onValuesChange={(_changes, v) => {
          onUnsaved(
            v.cantidad ||
              v.cantidad === 0 ||
              v.justificacion !== initialValues.justificacion,
          );
        }}
      >
        <Descriptions
          layout="horizontal"
          column={2}
          bordered
          labelStyle={{ maxWidth: '13rem' }}
        >
          <Descriptions.Item label="Producto" span={2}>
            <EllipsisModal
              title="Producto"
              text={initialValues.producto_nombre}
            />
          </Descriptions.Item>
          <Descriptions.Item label="Unidad">
            {initialValues.nombre_unitario}
          </Descriptions.Item>
          <Descriptions.Item label="Valor unidad">
            {ms}{' '}
            {initialValues.valor_unitario
              .toString()
              .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          </Descriptions.Item>
          <Descriptions.Item label="Unidades acumuladas para este pago">
            {initialValues.unidades_a_pagar || 0}
          </Descriptions.Item>
          <Descriptions.Item label="Valor unidades acumuladas para este pago">
            {ms}{' '}
            {(initialValues.valor_a_pagar || 0)
              .toString()
              .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          </Descriptions.Item>

          <Descriptions.Item
            label="Unidades para agregar a este pago"
            contentStyle={{ padding: '25px 25px 1px 25px' }}
          >
            <Form.Item
              name="cantidad"
              rules={[
                {
                  required: true,
                  message: 'Este campo es obligatorio!',
                },
                {
                  validator: (_, value) => {
                    if (!value) {
                      return Promise.resolve();
                    } else if (isNaN(value)) {
                      return Promise.reject(
                        new Error('Debe ser un valor numérico'),
                      );
                    }
                    if (value < 0) {
                      return Promise.reject(new Error('Debe ser positivo'));
                    }
                    if (
                      value.toString().includes('.') &&
                      value.toString().split('.', 2)[1].length > 2
                    ) {
                      return Promise.reject(
                        new Error('Debe ser un valor con máximo dos decimales'),
                      );
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber disabled={selfDisabled} />
            </Form.Item>
          </Descriptions.Item>
          <Descriptions.Item label="Valor unidades para agregar a este pago">
            <Form.Item noStyle dependencies={['cantidad']}>
              {({ getFieldValue }) => {
                const up = getFieldValue('cantidad') || 0;
                return `${ms} ${(up * initialValues.valor_unitario)
                  .toFixed(2)
                  .toString()
                  .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
              }}
            </Form.Item>
          </Descriptions.Item>
        </Descriptions>
        <Form.Item noStyle dependencies={['cantidad']}>
          {({ getFieldValue }) => {
            const up = getFieldValue('cantidad');
            if (
              acumulatedValue + initialValues.valor_unitario * (up || 0) >
                referenceValue ||
              up === 0
            ) {
              return (
                <>
                  <Alert
                    showIcon
                    message={
                      up === 0 ? (
                        <>
                          Si el contratista no entregó unidades de este producto
                          y desea registrar ese hecho para este pago, por favor
                          justifique.
                        </>
                      ) : (
                        <>
                          El valor aprobado para los productos sin tope superó
                          el <b>Valor máximo de referencia del tope interno</b>.
                          Si desea continuar, justifique.
                        </>
                      )
                    }
                    type="warning"
                  />
                  <Descriptions
                    layout="horizontal"
                    column={1}
                    bordered
                    labelStyle={{ maxWidth: '8rem' }}
                    contentStyle={{ padding: '25px 25px 1px 25px' }}
                  >
                    <Descriptions.Item label="Justificación de aprobación">
                      <Form.Item
                        name="justificacion"
                        rules={[
                          {
                            required: true,
                            message: 'Este campo es requerido',
                          },
                        ]}
                      >
                        <TextArea
                          disabled={selfDisabled}
                          rows={2}
                          bordered
                          allowClear
                        />
                      </Form.Item>
                    </Descriptions.Item>
                  </Descriptions>
                </>
              );
            }
            return null;
          }}
        </Form.Item>
        <div
          style={{
            border: '1px solid #f0f0f0',
            display: 'flex',
            justifyContent: 'space-evenly',
            padding: '10px',
          }}
        >
          <CustomButton
            text="Agregar a pago"
            type="primary"
            htmlType="button"
            onClick={() => form.submit()}
            disabled={selfDisabled || loading}
            loading={loading}
          />
          <CustomButton
            text="Ver unidades acumuladas a este pago"
            type="default"
            htmlType="button"
            onClick={onShowUnits}
            disabled={loading}
          />
        </div>
      </Form>
      <Modal
        open={modalOpen}
        closable={!loading}
        maskClosable={!loading}
        width="80%"
        title="Unidades agregadas a este pago"
        onCancel={() => {
          setModalOpen(false);
          setModalData();
        }}
        footer={null}
      >
        {modalData ? (
          <>
            <div>
              Total unidades aprobadas en pagos anteriores:{' '}
              <b>{modalData.prev_count}</b>, por un valor de{' '}
              <b>
                {ms}{' '}
                {modalData.prev_value
                  .toString()
                  .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              </b>
            </div>

            <Table
              rowKey="id"
              columns={unitsColumns}
              dataSource={modalData.results}
            />
          </>
        ) : (
          <Skeleton loading active />
        )}
      </Modal>
    </Card>
  );
}

export default function PAPaymentForm({
  record,
  contract,
  setAcumulated,
  reloadForm,
  setReloadForm,
  editPerm,
}) {
  const [products, setProducts] = useState();
  const [validationErrors, setValidationErrors] = useState();
  const [saveLoading, setSaveLoading] = useState();
  const [form] = Form.useForm();

  const [pstUnsaved, setPSTUnsaved] = useState({});

  useEffect(() => {
    async function fetchData() {
      setValidationErrors();
      setProducts();
      try {
        const res = await getProducts(record.id);
        setProducts(
          res.data.reduce((pv, cv) => {
            if (cv.tipo.endsWith('sT'))
              pv[`pst_${cv.producto}`] = {
                unidades_a_pagar: cv.unidades_a_pagar,
                valor_a_pagar: cv.valor_a_pagar,
                justificacion: cv.justificacion,
              };
            else pv[cv.id] = cv;
            return pv;
          }, {}),
        );
        form.resetFields();
      } catch (error) {
        catchException(error);
      }
    }

    if (!products || reloadForm?.paymentID === record.id) fetchData();
  }, [record.id, reloadForm]);

  const onUnitsAdded = async (values) => {
    try {
      const res = await add_units(record.id, values);
      setProducts((s) => ({
        ...s,
        [`pst_${values.producto}`]: {
          unidades_a_pagar: res.data.unidades_a_pagar,
          valor_a_pagar: res.data.valor_a_pagar,
          justificacion: values.justificacion,
        },
      }));
      setAcumulated(res.data.acumulado_referencia);
      setPSTUnsaved((s) => ({ ...s, [values.producto]: false }));
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: MESSAGES.DESC_SUCCESS_MSG,
        duration: 2,
      });
    } catch (error) {
      catchException(error);
    }
  };

  const onUnitsRemoved = async (values, product_id) => {
    try {
      const res = await remove_units(record.id, values);
      const subtractValue =
        products[`pst_${product_id}`].valor_a_pagar - res.data.valor_a_pagar;
      setProducts((s) => ({
        ...s,
        [`pst_${product_id}`]: {
          unidades_a_pagar: res.data.unidades_a_pagar,
          valor_a_pagar: res.data.valor_a_pagar,
          justificacion: s[`pst_${product_id}`]?.justificacion,
        },
      }));
      setAcumulated(contract.acumulado_referencia - subtractValue);
      setPSTUnsaved((s) => ({ ...s, [product_id]: false }));
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: MESSAGES.DESC_SUCCESS_DELETE_MSG,
        duration: 2,
      });
    } catch (error) {
      catchException(error);
    }
  };

  const onUnitsUnsaved = (pst_key, vf) => {
    setPSTUnsaved((s) => ({ ...s, [pst_key]: !!vf }));
  };

  const onSave = async (values, toSend = false) => {
    if (Object.values(pstUnsaved).some((vf) => vf === true)) {
      Modal.error({
        title: 'Unidades de productos/gastos sin tope',
        content: (
          <>
            Recuerde 'Agregar a pago' las unidades digitadas y/o los cambios en
            la justificación.
          </>
        ),
      });
      return;
    }
    if (contract.unicamente_sin_tope) {
      if (
        !Object.values(products).reduce((pv, cv) => {
          return pv || cv.unidades_a_pagar || cv.unidades_a_pagar === 0;
        }, undefined)
      ) {
        Modal.error({
          title: 'Unidades de productos/gastos sin tope',
          content: (
            <>
              Indique el número de unidades para al menos un producto/gasto sin
              tope. Recuerde 'Agregar a pago' las unidades digitadas.
            </>
          ),
        });
        return;
      }
    }
    function afterSave() {
      setReloadForm({ paymentID: record.id });
      setSaveLoading();
    }
    try {
      setSaveLoading(toSend ? 'send' : 'save');
      setValidationErrors();
      const res = await save_payment(record.id, {
        productos: values,
        to_send: toSend,
      });
      if (toSend) {
        Modal.confirm({
          title: 'Enviar a pago',
          content: (
            <>
              <p>
                Este pago se enviará con el estado: {res.data.estado_nombre}
              </p>
              <br />
              <p>¿Está seguro de continuar?</p>
            </>
          ),
          okText: 'Si',
          cancelText: 'No',
          onCancel: afterSave,
          onOk: async () => {
            try {
              await send_payment(record.id, {
                estado_aprobacion: res.data.estado_borrador,
              });
            } catch (error) {
              catchException(error);
            }
            afterSave();
          },
        });
      } else afterSave();
    } catch (err) {
      try {
        Modal.error({
          title: 'Existen errores de validación',
          content: (
            <>
              <p>Hay errores en la aprobación del pago.</p>
              {err.response.data.non_field_errors ? (
                <>
                  <Divider>PAGO No. {record.numero}</Divider>
                  <ul>
                    {err.response.data.non_field_errors.map((ei) => (
                      <li>{ei}</li>
                    ))}
                  </ul>
                </>
              ) : null}
              <>
                {Object.entries(err.response.data).map((v) => {
                  if (v[0].length === 36) {
                    const productV = products[v[0]];
                    return (
                      <>
                        <Divider orientation="left">
                          PAGO No. {record.numero} -{' '}
                          {productV.tipo.startsWith('P') ? 'Producto' : 'Gasto'}{' '}
                          {productV.tipo.endsWith('F') ? 'Fijo' : 'Variable'}{' '}
                          No. {productV.codigo}
                        </Divider>
                        <ul>
                          {Object.values(v[1])
                            .flat()
                            .map((ei) => (
                              <li>{ei}</li>
                            ))}
                        </ul>
                      </>
                    );
                  }
                  return null;
                })}
              </>
            </>
          ),
          width: '80%',
          okText: 'Entendido',
        });
        setValidationErrors(err.response.data);
      } catch (errv) {
        console.log(errv);
      }
      setSaveLoading();
    }
  };

  const handleSend = async (ask = true) => {
    if (contract.unicamente_sin_tope) {
      onSave({}, true);
      return;
    }
    if (
      ask &&
      contract.productos_st &&
      Object.keys(contract.productos_st).length > 0 &&
      Object.entries(products).reduce((pv, cv) => {
        if (!cv[0].startsWith('pst_')) {
          return pv;
        }
        return pv + cv.valor_a_pagar;
      }, 0) < 0.01
    ) {
      Modal.confirm({
        title: 'Enviar a pago',
        content: (
          <>
            El(los) productos/gastos sin topes no tiene unidades aprobadas
            <br />
            ¿Desea continuar?
          </>
        ),
        okText: 'Si',
        cancelText: 'No',
        onOk: () => handleSend(false),
      });
      return;
    }
    // Does the same as unit.submit(), but onSave recieves toSend as true
    form
      .validateFields()
      .then((values) => onSave(values, true))
      .catch((err) => {
        notification.error({
          message: MESSAGES.ERROR_MSG,
          description:
            'Existen errores de validación, favor revise los campos antes de enviar',
          duration: 2,
        });
        form.scrollToField(err.errorFields[0].name);
      });
  };

  if (!products) return <Skeleton active loading />;
  else
    return (
      <>
        {!contract.unicamente_sin_tope ? (
          <Form
            layout="horizontal"
            form={form}
            initialValues={products}
            scrollToFirstError
            onFinish={onSave}
            onValuesChange={(cv, _v) => {
              const ppId = Object.keys(cv)[0];
              if (products[ppId].tipo.endsWith('V')) {
                if (cv[ppId].hasOwnProperty('unidades_a_pagar')) {
                  const x = cv[ppId].unidades_a_pagar;
                  if (x === 0) {
                    form.setFieldsValue({
                      [ppId]: { ...cv[ppId], estado_aprobacion: undefined },
                    });
                  } else if (x > 0 && x < products[ppId].unidades) {
                    form.setFieldsValue({
                      [ppId]: { ...cv[ppId], estado_aprobacion: 101 },
                    });
                  } else if (x === products[ppId].unidades) {
                    form.setFieldsValue({
                      [ppId]: { ...cv[ppId], estado_aprobacion: 100 },
                    });
                  }
                } else if (cv[ppId].hasOwnProperty('estado_aprobacion')) {
                  const ea = cv[ppId].estado_aprobacion;
                  if ([95, 96].includes(ea)) {
                    form.setFieldsValue({
                      [ppId]: { ...cv[ppId], unidades_a_pagar: 0 },
                    });
                  }
                }
              }
            }}
          >
            {Object.entries(products)
              .filter((p) => !p[0].startsWith('pst_'))
              .map((p) => (
                <PAProductForm
                  key={p[0]}
                  initialValues={p[1]}
                  ms={contract.moneda_s}
                  currencyType={contract.moneda_sigla}
                  disabled={contract.has_pc || !editPerm}
                  paymentStatus={record.estado_aprobacion}
                  payNumber={record.numero}
                  saveErrors={validationErrors ? validationErrors[p[0]] : null}
                />
              ))}
          </Form>
        ) : null}
        {contract.productos_st
          ? Object.entries(contract.productos_st).map((p) => (
              <PAProductSTForm
                key={p[0]}
                paymentID={record.id}
                initialValues={{ ...p[1], ...(products[`pst_${p[0]}`] || {}) }}
                ms={contract.moneda_s}
                referenceValue={contract.valor_referencia}
                acumulatedValue={contract.acumulado_referencia}
                disabled={contract.has_pc || !editPerm}
                paymentStatus={record.estado_aprobacion}
                payNumber={record.numero}
                onAdd={onUnitsAdded}
                onRemove={onUnitsRemoved}
                onUnsaved={(vf) => onUnitsUnsaved(p[0], vf)}
              />
            ))
          : null}
        <br />
        {validationErrors && (
          <Alert
            message="Errores de validación:"
            type="error"
            showIcon
            style={{ marginBottom: '2rem' }}
            description={
              <>
                {validationErrors.non_field_errors ? (
                  <>
                    <Divider>PAGO No. {record.numero}</Divider>
                    <ul>
                      {validationErrors.non_field_errors.map((ei) => (
                        <li>{ei}</li>
                      ))}
                    </ul>
                  </>
                ) : null}
                <>
                  {Object.entries(validationErrors).map((v) => {
                    if (v[0].length === 36) {
                      const productV = products[v[0]];
                      return (
                        <>
                          <Divider orientation="left">
                            PAGO No. {record.numero} -{' '}
                            {productV.tipo.startsWith('P')
                              ? 'Producto'
                              : 'Gasto'}{' '}
                            {productV.tipo.endsWith('F') ? 'Fijo' : 'Variable'}{' '}
                            No. {productV.codigo}
                          </Divider>
                          <ul>
                            {Object.values(v[1])
                              .flat()
                              .map((ei) => (
                                <li>{ei}</li>
                              ))}
                          </ul>
                        </>
                      );
                    }
                    return null;
                  })}
                </>
              </>
            }
          />
        )}
        <div
          style={{
            // border: '1px solid #f0f0f0',
            display: 'flex',
            justifyContent: 'space-evenly',
            padding: '10px',
          }}
        >
          <CustomButton
            text="Guardar aprobaciones"
            htmlType="button"
            onClick={() => {
              if (!contract.unicamente_sin_tope) form.submit();
              else onSave({});
            }}
            type="default"
            loading={saveLoading === 'save'}
            disabled={
              saveLoading ||
              (record.estado_aprobacion &&
                ![107, 103].includes(record.estado_aprobacion)) ||
              !editPerm
            }
          />
          <CustomButton
            text="Guardar y enviar a pago"
            htmlType="button"
            onClick={handleSend}
            type="primary"
            loading={saveLoading === 'send'}
            disabled={
              saveLoading ||
              (record.estado_aprobacion &&
                ![107, 103].includes(record.estado_aprobacion)) ||
              !editPerm
            }
          />
        </div>
      </>
    );
}
